import { Injectable } from "@angular/core";
import { HttpParams, HttpHeaders } from "@angular/common/http";
import { Router } from "@angular/router";
import * as moment from "moment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { environment } from "../../../../../environments/environment";
import { Oauth2TokenService } from "../../../../../services/oauth2-token.service";
import { HttpInterceptor } from "../../../../shared/interceptors/http-interceptor.service";
import { Pagination } from "../models/pagination";
import { SearchResult } from "../models/search-result";
import { Vendedor } from "../models/vendedor";
import { VendedorConta } from "../models/vendedor-conta.model";
import { Vendedores } from "../models/vendedores";

@Injectable({
  providedIn: "root",
})
export class VendedoresService {
  private vendedoresUrl: string;
  private vendedorUpdatePlanUrl: string;
  private vendedoresGridUrl: string;
  private vendedorDeleteUrl: string;
  private organizacaoUrl: string;
  private vendedoresLoginUrl: string;
  private zoopSyncUrl: string;
  private bancoUrl: string;
  private contaUrl: string;
  private apiV1Url: string;
  private apiV2Url: string;
  vendedores: Vendedores[] = [];
  pagination: Pagination[] = [];

  constructor(
    private http: HttpInterceptor,
    private router: Router,
    private oauth2Token: Oauth2TokenService
  ) {
    this.vendedoresUrl = environment.api.base_url + "/api/v1/vendedor";
    this.vendedorUpdatePlanUrl =
      environment.api.base_url + "/api/v1/particular/update";
    this.vendedoresGridUrl = environment.api.base_url + "/api/v1/vendedor/grid";
    this.vendedorDeleteUrl = environment.api.base_url + "/api/v1/vendedor";
    this.organizacaoUrl = environment.api.base_url + "/api/v1/organizacao";
    this.zoopSyncUrl =
      environment.api.base_url + "/api/v1/zoop/sync/transaction";
    this.contaUrl = environment.api.base_url + "/api/v1/conta";
    this.apiV1Url = environment.api.base_url + "/api/v1";
    this.apiV2Url = environment.api.base_url + "/api/v2";
    this.bancoUrl = environment.api.base_url + "/api/v1/banco";
  }

  async getSearchResult(params?: HttpParams): Promise<SearchResult> {
    const headers = this.getHeaders();
    return this.http
      .get(this.vendedoresUrl, { headers, params })
      .toPromise()
      .then((response) => {
        let result = new SearchResult();
        let responseJson = response;
        result.vendedores = responseJson.data as any[];
        result.pagination = responseJson.meta.pagination as Pagination;
        return result;
      })
      .catch(this.handleError);
  }

  async getSearchResultGrid(params?: HttpParams): Promise<SearchResult> {
    const headers = this.getHeaders();
    return this.http
      .get(this.vendedoresGridUrl, { headers, params })
      .toPromise()
      .then((response) => {
        let result = new SearchResult();
        let responseJson = response;
        result.vendedores = responseJson.data as any[];
        result.pagination = responseJson.meta.pagination as Pagination;
        return result;
      })
      .catch(this.handleError);
  }

  private handleError(error: any): Promise<any> {
    console.error("An error occurred", error); // for demo purposes only
    return Promise.reject(error.message || error);
  }

  async getVendedor(id: string): Promise<Vendedores> {
    let params = new HttpParams()
      .set("vendedor_id", id)
      .set("include", "organizacao");
    return this.getSearchResultGrid(params).then((result) =>
      result.vendedores.find((vendedor) => vendedor.vendedor_id === id)
    );
  }

  async getVendedorOutsideGrid(id: string): Promise<Vendedores> {
    let params = new HttpParams()
      .set("id", id)
      .set("include", "detalhe,detalhe.endereco,detalhe.dono_endereco,plano");
    return this.getSearchResult(params).then((result) =>
      result.vendedores.find((vendedor) => vendedor.id === id)
    );
  }

  getOrganizacao(params?: HttpParams): Observable<Array<any>> {
    const headers = this.getHeaders();
    let url = this.organizacaoUrl;

    return this.http
      .get(url, { params, headers })
      .pipe(map((res: any) => res.data));
  }

  getConta(params?: HttpParams): Observable<Array<any>> {
    const headers = this.getHeaders();
    let url = this.contaUrl;

    return this.http
      .get(url, { params, headers })
      .pipe(map((res: any) => res.data));
  }

  updateConta(conta: any) {
    const headers = this.getHeaders();
    let url = this.contaUrl + `/${conta.id}`;

    return this.http
      .put(url, conta, { headers: headers })
      .pipe(map((res: any) => res));
  }

  async update(vendedor: any, data: any): Promise<void> {
    const url = `${this.vendedoresUrl}/${vendedor.id}`;
    const headers = this.getHeaders();
    return this.http
      .patch(
        url,
        JSON.stringify({
          envio_data: data,
          envio_email: vendedor.envio_email,
          envio_rastreio: vendedor.envio_rastreio,
        }),
        { headers }
      )
      .toPromise()
      .then((res) => {
        let responseJson = res;
        this.router.navigate([
          "/pages/vendedores/vendedores-detail/",
          responseJson.data.id,
        ]);
      });
  }

  updatePlanAndDocuments(vendedorUpdate: FormData) {
    let headers = this.getHeaders();
    headers = headers.delete("Content-Type");
    let url = environment.api.base_api + "particular/update";

    return this.http
      .post(url, vendedorUpdate, { headers: headers })
      .pipe(map((res: any) => res));
  }

  async updateDadosVendedor(vendedor): Promise<void> {
    const url = `${this.vendedoresUrl}/${vendedor.id}`;
    const headers = this.getHeaders();
    return this.http
      .put(url, JSON.stringify(vendedor), { headers })
      .toPromise()
      .then((res) => {
        let responseJson = res;
        return responseJson;
      });
  }

  async updatePlan(vendedor): Promise<void> {
    const url = `${this.vendedorUpdatePlanUrl}`;
    const headers = this.getHeaders();
    return this.http
      .post(url, JSON.stringify(vendedor), { headers })
      .toPromise()
      .then((res) => {
        let responseJson = res;
        return responseJson;
      });
  }

  create(vendedor: Vendedor) {
    let url = environment.api.base_api + "vendedor";
    const headers = this.getHeaders();
    return this.http
      .post(url, vendedor, { headers })
      .pipe(map((res: any) => res));
  }

  createConta(conta: VendedorConta) {
    let url = environment.api.base_api + "conta";
    const headers = this.getHeaders();
    return this.http.post(url, conta, { headers }).pipe(map((res: any) => res));
  }

  async createDadosLogin(
    plano_saude_id: string,
    nome: string,
    descricao: string,
    obrigatorio: boolean
  ): Promise<Vendedores> {
    const headers = this.getHeaders();
    return this.http
      .post(
        this.vendedoresLoginUrl,
        JSON.stringify({ plano_saude_id, nome, descricao, obrigatorio }),
        { headers }
      )
      .toPromise()
      .then((res) => res);
  }

  async delete(id: string): Promise<void> {
    const url = `${this.vendedorDeleteUrl}/${id}`;
    const headers = this.getHeaders();
    return this.http
      .delete(url, { headers })
      .toPromise()
      .then((res) => {
        let responseJson = res;
        this.router.navigate(["/pages/vendedores/"]);
      });
  }

  async deleteDadosLogin(id: string): Promise<void> {
    const url = `${this.vendedoresLoginUrl}/${id}`;
    const headers = this.getHeaders();
    return this.http
      .delete(url, { headers })
      .toPromise()
      .then((res) => {
        let responseJson = res;
      });
  }

  async sync(
    organizacao_id: string,
    vendedor_id: string,
    data_inicio: string,
    data_fim: string
  ): Promise<void> {
    const headers = this.getHeaders();
    return this.http
      .post(
        this.zoopSyncUrl,
        JSON.stringify({
          organizacao_id,
          vendedor_id,
          data_inicio,
          data_fim,
          mostrar_requisicoes: true,
        }),
        { headers }
      )
      .toPromise()
      .then((res) => res);
  }

  syncZoopStatus(data: any) {
    let url =
      environment.api.base_api + "vendedor/webHookZoopAtualizacaoDeStatus";
    const headers = this.getHeaders();
    return this.http
      .post(url, data, { headers: headers })
      .pipe(map((res: any) => res));
  }

  enablePOS(organizationId: string, sellerId: string, token: string) {
    let url = `${this.apiV2Url}/organizations/${organizationId}/sellers/${sellerId}/terminal/pair`;
    const terminalToken = {
      terminal_token: token,
    };
    const headers = this.getHeaders();
    return this.http
      .post(url, terminalToken, { headers })
      .pipe(map((res: any) => res));
  }

  setFraudster(organizationId: string, sellerId: string, description: string) {
    let url = `${this.apiV1Url}/vendedor_motivo_sob_suspeita`;
    const body = {
      organizacao_id: organizationId,
      vendedor_id: sellerId,
      descricao: description,
      data_ocorrencia: moment(new Date()).format("YYYY/MM/DD HH:mm:ss"),
    };
    const headers = this.getHeaders();

    return this.http.post(url, body, { headers }).pipe(map((res: any) => res));
  }

  setFraudsterSeller(
    organizationId: string,
    sellerId: string,
    description: string
  ) {
    let url = `${
      this.vendedoresUrl
    }/${sellerId}/sob_suspeita?organizacao_id=${organizationId}&descricao=${description}&data_ocorrencia=${moment(
      new Date()
    ).format("YYYY/MM/DD HH:mm:ss")}`;
    const headers = this.getHeaders();
    return this.http.put(url, null, { headers }).pipe(map((res: any) => res));
  }

  deleteFraudsterStatus(sellerId: string) {
    let url = `${this.apiV1Url}/vendedor_motivo_sob_suspeita/${sellerId}`;
    const headers = this.getHeaders();
    return this.http.delete(url, { headers }).pipe(map((res: any) => res));
  }

  getFraudsterSeller(sellerId: string) {
    let url = `${this.apiV1Url}/vendedor_motivo_sob_suspeita?include=vendedor,antigo_plano,novo_plano&vendedor_id=${sellerId}`;
    const headers = this.getHeaders();
    return this.http.get(url, { headers }).pipe(map((res: any) => res));
  }

  getBanco(params: HttpParams) {
    let headers = this.getHeaders();
    let url = this.bancoUrl;

    return this.http.get(url, { params, headers }).pipe(map((res: any) => res));
  }

  async getTransfers(
    sellerId: string,
    organizationId: string,
    params?: HttpParams
  ): Promise<SearchResult> {
    let url = `${this.apiV2Url}/organizations/${organizationId}/sellers/${sellerId}/transfers`;
    return this.http
      .get(url, { headers: this.getHeaders(), params })
      .toPromise()
      .then((response) => {
        let result = new SearchResult();
        let responseJson = response;
        result.transfers = responseJson.data;
        result.pagination = responseJson.meta.pagination as Pagination;

        return result;
      })
      .catch(this.handleError);
  }

  async getTransfersReceivables(
    sellerId: string,
    organizationId: string,
    transferId: string,
    params?: HttpParams
  ): Promise<SearchResult> {
    let url = `${this.apiV2Url}/organizations/${organizationId}/sellers/${sellerId}/transfers/${transferId}/history`;
    return this.http
      .get(url, { headers: this.getHeaders(), params })
      .toPromise()
      .then((response) => {
        let result = new SearchResult();
        result.transferReceivables = response.data;
        result.pagination = response.meta as Pagination;

        return result;
      })
      .catch(this.handleError);
  }

  getHeaders() {
    const accessToken = this.oauth2Token.getTokens().accessToken;

    return new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    });
  }
}
